import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Location from "@mapmycustomers/shared/types/Location";
import User from "@mapmycustomers/shared/types/User";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { Feature, useMap } from "@app/component/map/FeaturedMap";
import UserPreviewCard from "@app/component/UserPreviewCard";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import { highlightLocationGroupId, initSideBar, selectUser } from "@app/scene/map/store/actions";
import {
  getFirstLocationId,
  getHighlightedLocationGroup,
  getSelectedUser,
  getUserLocationGroups,
  isSideBarInitializing,
  isUserLocationLoading,
} from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import {
  getUserColorClassNameGetter,
  UserColorClassNameGetter,
} from "@app/store/userColor/selectors";
import colors from "@app/styles/_colors.module.scss";
import LocationGroup from "@app/types/map/LocationGroup";
import defaultIdGetter from "@app/util/defaultIdGetter";
import { userDisplayName } from "@app/util/formatters";
import { USER_LOCATION_HISTORY_PATH_ZINDEX } from "@app/util/map/consts";

import LocationRecords from "../LocationRecords";

import LocationFilters from "./component/LocationFilters";
import UserLocationGroupTooltip from "./component/UserLocationGroupTooltip";
import styles from "./SideBar.module.scss";
import locationGeometryGetter from "./util/locationGeometryGetter";
import locationStyleGetter from "./util/locationStyleGetter";

const isDifferent = (a: LocationGroup, b: LocationGroup) => a.id !== b.id;

const pathSymbol = {
  path: "M 0,-1 0,2",
  scale: 4,
  strokeOpacity: 1,
};

const POLYLINE_CONFIG = {
  clickable: false,
  icons: [
    {
      icon: pathSymbol,
      offset: "0",
      repeat: "20px",
    },
  ],
  strokeColor: colors.primary100,
  strokeOpacity: 0,
  strokeWeight: 1,
  zIndex: USER_LOCATION_HISTORY_PATH_ZINDEX,
};

interface Props {
  firstLocationId?: Location["id"];
  getUserColorClassName: UserColorClassNameGetter;
  highlightedLocationGroupId?: LocationGroup["id"];
  highlightLocationGroupId: typeof highlightLocationGroupId;
  initSideBar: typeof initSideBar.request;
  loading: boolean;
  locationGroups: LocationGroup[];
  selectUser: typeof selectUser;
  user?: User;
  userIdToBase64Avatar: Map<User["id"], string>;
}

const Sidebar: React.FC<Props> = ({
  firstLocationId,
  getUserColorClassName,
  highlightedLocationGroupId,
  highlightLocationGroupId,
  initSideBar,
  loading,
  locationGroups,
  selectUser,
  user,
  userIdToBase64Avatar,
}) => {
  const pathPolyline = useRef(new google.maps.Polyline(POLYLINE_CONFIG));
  const intl = useIntl();
  const [clickedLocationGroupId, setClickedLocationGroupId] = useState<
    LocationGroup["id"] | undefined
  >();
  const [hoveredLocationGroup, setHoveredLocationGroup] = useState<LocationGroup | undefined>();

  const { map } = useMap();

  useEffect(() => {
    initSideBar();
  }, [initSideBar, user]);

  useEffect(() => {
    const pathPoly = pathPolyline.current;
    return () => {
      pathPoly.setMap(null);
    };
  }, []);

  const handleHide = useCallback(() => {
    pathPolyline.current.setMap(null);
    selectUser(undefined);
  }, [pathPolyline, selectUser]);

  useEffect(() => {
    pathPolyline.current.getPath().clear();
    pathPolyline.current.setMap(map);
    locationGroups.forEach((group) => {
      const position = new google.maps.LatLng(
        group.locations[0].geoPoint.coordinates[1],
        group.locations[0].geoPoint.coordinates[0]
      );
      pathPolyline.current.getPath().push(position);
    });
  }, [locationGroups, map, pathPolyline]);

  const handleSelectLocation = useCallback(
    (locationGroup: LocationGroup) => {
      highlightLocationGroupId(locationGroup.id);
      setHoveredLocationGroup(locationGroup);
    },
    [highlightLocationGroupId, setHoveredLocationGroup]
  );

  const handleClearSelectedLocation = useCallback(() => {
    highlightLocationGroupId(undefined);
    setHoveredLocationGroup(undefined);
  }, [highlightLocationGroupId, setHoveredLocationGroup]);

  const locationPinGetter = useMemo(() => {
    return locationStyleGetter(
      {
        clickedLocationGroupId,
        firstLocationId,
        highlightedLocationGroupId,
        user,
        userIdToBase64Image: userIdToBase64Avatar,
      },
      getUserColorClassName
    );
  }, [
    firstLocationId,
    getUserColorClassName,
    highlightedLocationGroupId,
    user,
    userIdToBase64Avatar,
    clickedLocationGroupId,
  ]);

  const handleCentreOnLocation = useCallback(
    (location: Location) => {
      map.panTo({
        lat: location.geoPoint.coordinates[1],
        lng: location.geoPoint.coordinates[0],
      });
    },
    [map]
  );

  const handleLocationClick = useCallback(
    (locationGroup: LocationGroup) => {
      setClickedLocationGroupId(locationGroup.id);
      handleCentreOnLocation(locationGroup.locations[0]);
    },
    [handleCentreOnLocation, setClickedLocationGroupId]
  );

  if (!user) {
    return null;
  }

  return (
    <>
      <BaseSidebar
        className={styles.sidebar}
        mask={false}
        onClose={handleHide}
        title={
          <UserPreviewCard userId={user.id}>
            <Tooltip title={user.username}>{userDisplayName(user)}</Tooltip>
          </UserPreviewCard>
        }
        visible
        widthKey="user-location-sidebar"
      >
        <div className={styles.content}>
          <div className={styles.filterContainer}>
            <div>
              {intl.formatMessage({
                id: "map.layer.userLocation.filter",
                defaultMessage: "Show on map",
                description: "Map - user location pane - filter text",
              })}
            </div>
            <LocationFilters />
          </div>
          <div className={styles.locationsWrapper}>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <LocationRecords
                clickedLocationGroupId={clickedLocationGroupId}
                locationGroups={locationGroups}
                onClick={handleLocationClick}
              />
            )}
          </div>
        </div>
      </BaseSidebar>
      <Feature<LocationGroup>
        geometryGetter={locationGeometryGetter}
        idGetter={defaultIdGetter}
        isDifferent={isDifferent}
        items={locationGroups}
        onClick={handleLocationClick}
        onMouseLeave={handleClearSelectedLocation}
        onMouseOver={handleSelectLocation}
        styleGetter={locationPinGetter}
        type="userLocationItems"
      />
      {hoveredLocationGroup && <UserLocationGroupTooltip locationGroup={hoveredLocationGroup} />}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  firstLocationId: getFirstLocationId(state),
  getUserColorClassName: getUserColorClassNameGetter(state),
  highlightedLocationGroupId: getHighlightedLocationGroup(state),
  loading: isUserLocationLoading(state) || isSideBarInitializing(state),
  locationGroups: getUserLocationGroups(state),
  user: getSelectedUser(state),
});

const mapDispatchToProps = {
  highlightLocationGroupId,
  initSideBar: initSideBar.request,
  selectUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
